import { computeHash } from './perceptual-hash';

import { mediaService } from '@/services/media/mediaService';
import { logger } from '@/utils/logger';

interface MediaItem {
  id: string;
  url: string;
  type: string;
  metadata: { phash_status?: string; phash?: string; [k: string]: unknown } | null;
}

const REHASH_LOCK_KEY = 'mora-phash-rehash-in-flight';
const REHASH_BATCH_LIMIT = 5;

/**
 * Scans loaded media for items marked phash_status='pending_client_rehash'
 * and rehashes up to REHASH_BATCH_LIMIT of them. Fire-and-forget.
 *
 * Uses a sessionStorage lock to avoid concurrent rehash from multiple tabs/views
 * racing on the same row.
 */
export async function rehashPendingMedia(items: MediaItem[]): Promise<void> {
  if (typeof window === 'undefined') return;

  const pending = items
    .filter((item) => item.type?.startsWith('image/'))
    .filter((item) => item.metadata?.phash_status === 'pending_client_rehash')
    .filter((item) => !item.metadata?.phash)
    .slice(0, REHASH_BATCH_LIMIT);

  if (pending.length === 0) return;

  const lockTime = sessionStorage.getItem(REHASH_LOCK_KEY);
  if (lockTime) {
    const ageMs = Date.now() - Number.parseInt(lockTime, 10);
    if (ageMs < 30_000) return;
  }
  sessionStorage.setItem(REHASH_LOCK_KEY, String(Date.now()));

  try {
    for (const item of pending) {
      try {
        const res = await fetch(item.url, { mode: 'cors' });
        if (!res.ok) continue;
        const blob = await res.blob();
        if (!blob.type.startsWith('image/')) continue;
        const file = new File([blob], 'media', { type: blob.type });
        const phash = await computeHash(file);
        await mediaService.patchMetadata(item.id, { phash, phash_source: 'client-dct-rehash' });
      } catch (err) {
        logger.error('[opportunisticRehash] failed for media item', { id: item.id, err });
      }
    }
  } finally {
    sessionStorage.removeItem(REHASH_LOCK_KEY);
  }
}
